import { Lightning } from '@lightningjs/sdk';

export class SpinnerShader extends Lightning.shaders.WebGLDefaultShader {
  constructor(ctx) {
    super(ctx);
    this._progress = 0.0;
  }

  get progress() {
    return this._progress;
  }

  set progress(value) {
    this._progress = value;
    this.redraw();
  }

  useDefault() {
    return super.useDefault() && this._progress === 0.0;
  }

  setupUniforms(operation) {
    super.setupUniforms(operation);
    const { gl } = this;
    this._setUniform('projection', this._getProjection(operation), gl.uniform2fv, false);
    this._setUniform('progress', this._progress, gl.uniform1f);
  }

  getExtraAttribBytesPerVertex() {
    return 8;
  }

  enableAttribs() {
    super.enableAttribs();
    this.gl.enableVertexAttribArray(this._attrib('aVertex'));
  }

  disableAttribs() {
    super.disableAttribs();
    this.gl.disableVertexAttribArray(this._attrib('aVertex'));
  }

  setExtraAttribsInBuffer(operation) {
    let offset = operation.extraAttribsDataByteOffset / 4;
    const { floats } = operation.quads;

    const { length } = operation;
    for (let i = 0; i < length; i += 1) {
      /**
        1.0, -1.0,
        1.0,  1.0,
       -1.0,  1.0,
       -1.0, -1.0,
      */
      floats[offset + 0] = 1;
      floats[offset + 1] = -1;
      floats[offset + 2] = 1;
      floats[offset + 3] = 1;
      floats[offset + 4] = -1;
      floats[offset + 5] = 1;
      floats[offset + 6] = -1;
      floats[offset + 7] = -1;

      offset += 8;
    }
  }

  beforeDraw(operation) {
    const { gl } = this;
    gl.vertexAttribPointer(
      this._attrib('aVertex'),
      2,
      gl.FLOAT,
      false,
      this.getExtraAttribBytesPerVertex(),
      this.getVertexAttribPointerOffset(operation),
    );
  }
}

SpinnerShader.vertexShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    attribute vec2 aVertexPosition;
    attribute vec2 aVertex;
    attribute vec4 aColor;

    varying vec4 vColor;
    varying vec2 vPosition;

    uniform vec2 projection;

    void main(void) {
        gl_Position = vec4(aVertexPosition.x * projection.x - 1.0, aVertexPosition.y * -abs(projection.y) + 1.0, 0.0, 1.0);
        gl_Position.y = -sign(projection.y) * gl_Position.y;

        vPosition = aVertex;
        vColor = aColor;
    }
`;

SpinnerShader.fragmentShaderSource = `
    #ifdef GL_ES
    # ifdef GL_FRAGMENT_PRECISION_HIGH
    precision highp float;
    # else
    precision lowp float;
    # endif
    #endif

    #define PIx2 6.28318530718

    varying vec4 vColor;
    varying vec2 vPosition;

    uniform float progress;

    void main () {
      float distance = sqrt(dot(vPosition, vPosition));
      float externalRadius = 1.0;
      float internalRadius = 0.86;

      if (distance >= externalRadius || distance <= internalRadius) {
        discard;
      }

      float angle = atan(vPosition.x, vPosition.y) / PIx2;
      if (angle < 0.0) {
        angle = 1.0 + angle;
      }
      angle = 1.0 - angle;
      if (angle > progress) {
        discard;
      }

      gl_FragColor = vColor;
    }
`;
